
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, Globe, BarChart, Wifi, Database, Code, Award, Users, Target, ShieldCheck, Laptop } from 'lucide-react';

export const Landing: React.FC = () => {
  const [certId, setCertId] = useState('');
  const navigate = useNavigate();

  // const handleVerify = () => {
  //   const raw = certId.trim();
  //   if (raw) {
  //     const slug = raw.replace(/\//g, '-');
  //     navigate(`/${slug}`);
  //   }
  // };

const handleVerify = async () => {
  const raw = certId.trim();
  if (!raw) {
    alert('Please enter a certificate ID');
    return;
  }
  
  try {
    // Convert any hyphens to slashes for the API call
    const certificateId = raw.replace(/-/g, '/');
    // Navigate to the verification page with the original ID (with hyphens for URL)
    const urlSafeId = raw.replace(/\//g, '-');
    navigate(`/verify/${urlSafeId}`);
  } catch (error) {
    console.error('Verification error:', error);
    alert('Failed to verify certificate. Please try again.');
  }
};

  return (
    <div className="w-full overflow-hidden">
      {/* Hero Section */}
      <section className="bg-white py-16 lg:py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Content */}
            <div className="space-y-8 animate-[fadeInLeft_1s_ease-out]">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Verify Certificates <span className="text-primary">Instantly</span>
              </h1>
              <p className="text-lg text-gray-700 font-medium">
                Fast, Simple and Trusted. Ensure the authenticity of Speedlink certifications with our secure verification system.
              </p>
              
              <div className="bg-white p-1 rounded-lg max-w-md">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    placeholder="Enter Certificate ID (IN CAPITAL LETTERS)"
                    value={certId}
                    onChange={(e) => setCertId(e.target.value)}
                    className="flex-grow px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 bg-gray-50 text-gray-900 placeholder-gray-500 placeholder:text-xs placeholder:font-semibold"
                  />
                  <button 
                    onClick={handleVerify}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md transition-colors whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Verify Now
                  </button>
                </div>
                <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    
                    <span className="text-xs text-gray-500 font-semibold">Issued by: Speedlink Hi-Tech Solutions Limited.</span>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative flex justify-center animate-[fadeInRight_1s_ease-out]">
              <div className="bg-gray-100 rounded-2xl p-8 w-full max-w-lg aspect-square flex items-center justify-center relative overflow-hidden shadow-xl">
                 {/* Abstract illustrative shapes */}
                 <div className="absolute top-10 right-10 w-20 h-20 bg-purple-200 rounded-full blur-xl opacity-50 animate-pulse"></div>
                 <div className="absolute bottom-10 left-10 w-32 h-32 bg-blue-200 rounded-full blur-xl opacity-50 animate-pulse"></div>
                 <img 
                 src={'/verify/hero-image.png'} 
                 alt="Speedlink Students" 
                 className="relative z-10 w-4/5 h-auto object-contain drop-shadow-2xl rounded-lg transform hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses/Categories Section */}
      <section className="bg-primary py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl font-bold text-white mb-10 animate-[fadeInUp_1s_ease-out]">Kickstart Your Tech Career Today</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <CategoryCard icon={<Globe />} title="Digital Marketing" />
            <CategoryCard icon={<Wifi />} title="Cloud Networking" />
            <CategoryCard icon={<BarChart />} title="Data Analytics" />
            <CategoryCard icon={<Laptop />} title="Multimedia Solutions" />
            <CategoryCard icon={<ShieldCheck />} title="Cybersecurity" />
            <CategoryCard icon={<Database />} title="Fibre Optics" />
            <CategoryCard icon={<Laptop />} title="UI/UX Design" />
            <CategoryCard icon={<Code />} title="Full Stack Development" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-primary font-bold text-sm uppercase tracking-wider mb-2 block">Who We Are</span>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">About Speedlink</h2>
                <div className="h-1 w-20 bg-primary mx-auto mb-6"></div>
                <p className="text-lg text-gray-700 leading-relaxed font-medium">
                    Speedlink Hi-Tech Solutions Limited is a world-class IT training institute and software development company. We are committed to redefining the IT industry in Nigeria and Africa at large by providing professional, practical, and project-based training.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center text-primary mb-6">
                        <Award className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">World-Class Training</h3>
                    <p className="text-gray-600 font-medium">
                        Our training programs are designed to equip individuals and corporate organizations with cutting-edge technical skills required to thrive in today's digital economy.
                    </p>
                </div>
                <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center text-red-600 mb-6">
                        <Users className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Expert Mentorship</h3>
                    <p className="text-gray-600 font-medium">
                        With a team of seasoned professionals and state-of-the-art facilities, we ensure our students receive the mentorship and hands-on experience needed to excel.
                    </p>
                </div>
                <div className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="w-14 h-14 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-900 mb-6">
                        <Target className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Career Growth</h3>
                    <p className="text-gray-600 font-medium">
                         We are your partner in progress for Software Engineering, Data Science, Cyber Security, and Digital Literacy, ensuring you are market-ready.
                    </p>
                </div>
            </div>
        </div>
      </section>

      {/* Inline Styles for animations */}
      <style>{`
        @keyframes fadeInLeft {
            from { opacity: 0; transform: translateX(-20px); }
            to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInRight {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const CategoryCard: React.FC<{ icon: React.ReactNode, title: string }> = ({ icon, title }) => (
  <div className="bg-white rounded-lg p-4 flex items-center gap-4 hover:shadow-lg transition-all duration-300 cursor-pointer group transform hover:-translate-y-1">
    <div className="text-primary p-2 bg-blue-50 rounded-md group-hover:bg-primary group-hover:text-white transition-colors">
      {icon}
    </div>
    <span className="text-sm font-bold text-gray-800 text-left">{title}</span>
  </div>
);
